import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, ToggleLeft, ToggleRight, Check, X } from 'lucide-react';
import { SubscriptionPlan } from '@/hooks/usePlans';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PlansDetailsTable } from './PlansDetailsTable';

interface PlansTableProps {
  plans: SubscriptionPlan[];
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: (id: string, name: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
}

export function PlansTable({ plans, onEdit, onDelete, onToggleStatus }: PlansTableProps) {
  // Sort all plans by monthly price
  const sortedPlans = [...plans].sort((a, b) => a.price_monthly - b.price_monthly);

  if (sortedPlans.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">Nenhum plano encontrado</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="border rounded-lg overflow-hidden">
        <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Plano</TableHead>
            <TableHead>Preço Mensal</TableHead>
            <TableHead>Preço Anual</TableHead>
            <TableHead>Preço Vitalício</TableHead>
            <TableHead>Usuários</TableHead>
            <TableHead>Produtos</TableHead>
            <TableHead>Integrações</TableHead>
            <TableHead>Métricas Básicas</TableHead>
            <TableHead>Métricas Avançadas</TableHead>
            <TableHead className="text-center">Metas</TableHead>
            <TableHead className="text-center">Lançamento Manual</TableHead>
            <TableHead className="text-center">Importação em Massa</TableHead>
            <TableHead className="text-center">Suporte Prioritário</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedPlans.map((plan) => (
            <TableRow key={plan.id} className={!plan.is_active ? 'opacity-60' : ''}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span className={!plan.is_active ? 'text-muted-foreground' : ''}>
                    {plan.name}
                  </span>
                  {!plan.is_active && <Badge variant="secondary">Inativo</Badge>}
                </div>
              </TableCell>
              <TableCell>R$ {plan.price_monthly.toFixed(2)}</TableCell>
              <TableCell>
                {plan.price_annual > 0 ? `R$ ${plan.price_annual.toFixed(2)}` : '-'}
              </TableCell>
              <TableCell>
                {plan.price_lifetime > 0 ? `R$ ${plan.price_lifetime.toFixed(2)}` : '-'}
              </TableCell>
              <TableCell>{plan.max_users}</TableCell>
              <TableCell>
                {(plan as any).max_products === 0 ? 'Ilimitado' : (plan as any).max_products}
              </TableCell>
              <TableCell>
                {plan.integrations_mode === 'customizable'
                  ? plan.max_integrations
                  : plan.allowed_integrations.length}
              </TableCell>
              <TableCell>{plan.basic_metrics.length}</TableCell>
              <TableCell>
                {plan.advanced_metrics_enabled
                  ? plan.advanced_metrics_mode === 'customizable'
                    ? plan.max_advanced_metrics
                    : plan.advanced_metrics.length
                  : '-'}
              </TableCell>
              <TableCell className="text-center">
                {plan.goals_enabled ? (
                  <Check className="h-4 w-4 text-green-600 mx-auto" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground mx-auto" />
                )}
              </TableCell>
              <TableCell className="text-center">
                {plan.manual_entry_enabled ? (
                  <Check className="h-4 w-4 text-green-600 mx-auto" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground mx-auto" />
                )}
              </TableCell>
              <TableCell className="text-center">
                {plan.bulk_import_enabled ? (
                  <Check className="h-4 w-4 text-green-600 mx-auto" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground mx-auto" />
                )}
              </TableCell>
              <TableCell className="text-center">
                {plan.priority_support ? (
                  <Check className="h-4 w-4 text-green-600 mx-auto" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground mx-auto" />
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={plan.is_active ? "default" : "outline"}
                        size="sm"
                        onClick={() => onEdit(plan)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Editar configurações do plano</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={plan.is_active ? "default" : "outline"}
                        size="sm"
                        onClick={() => onToggleStatus(plan.id, !plan.is_active)}
                      >
                        {plan.is_active ? (
                          <ToggleRight className="h-4 w-4" />
                        ) : (
                          <ToggleLeft className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{plan.is_active ? 'Desativar plano' : 'Ativar plano'}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDelete(plan.id, plan.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Excluir plano permanentemente</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>

    {sortedPlans.length > 0 && <PlansDetailsTable plans={sortedPlans} />}
    </TooltipProvider>
  );
}
